import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus } from 'lucide-react'
import { Badge, Button, Card, Input, Modal, Select, Spinner } from '@/components/ui'
import { usePermissions } from '@/hooks/usePermissions'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { cn } from '@/lib/cn'
import { SetupSectionIntro } from '@/features/hr/components/hrShared'
import {
  createHrStructure, listHrStructure, updateHrStructure,
  type HrStructureKind, type HrStructureRecord,
} from '@/api/services/hr-structure.service'

/**
 * Every list, in the order people usually build them: the departments first,
 * then what hangs off them, then the labels that describe a person's job.
 */
const KINDS: Array<{
  kind: HrStructureKind
  label: string
  singular: string
  description: string
  example: string
}> = [
  {
    kind: 'department', label: 'Departments', singular: 'department', example: 'Finance',
    description: 'The main parts of the company, such as Finance or Operations. A department can sit inside another one, one level deep.',
  },
  {
    kind: 'team', label: 'Teams', singular: 'team', example: 'Payroll',
    description: 'Smaller groups inside a department, such as Payroll inside Finance. Every team belongs to one department.',
  },
  {
    kind: 'job_title', label: 'Job titles', singular: 'job title', example: 'Accountant',
    description: 'What a person’s role is called, such as Accountant or Shift Supervisor.',
  },
  {
    kind: 'position', label: 'Positions', singular: 'position', example: 'Senior Accountant',
    description: 'Seats you plan to fill, with a planned headcount and whether the seat is open, filled or paused.',
  },
  {
    kind: 'grade', label: 'Grades', singular: 'grade', example: 'G3',
    description: 'Seniority or pay bands, such as G1 to G5. Policies can be aimed at particular grades.',
  },
  {
    kind: 'employment_type', label: 'Employment types', singular: 'employment type', example: 'Full-time',
    description: 'How someone is employed, such as Full-time, Part-time or Contract.',
  },
]

const kindInfo = (kind: HrStructureKind): (typeof KINDS)[number] =>
  KINDS.find((item) => item.kind === kind) ?? KINDS[0]

const VACANCY_LABELS = { open: 'Open', filled: 'Filled', paused: 'Paused' } as const

interface FormState {
  kind: HrStructureKind
  name: string
  code: string
  parentId: string
  headcount: string
  vacancyState: string
}

const emptyForm: FormState = {
  kind: 'department', name: '', code: '', parentId: '', headcount: '', vacancyState: '',
}

export function CompanyStructureTab({ companyId }: { companyId: string }): React.ReactElement {
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canEdit = can('hr', 'structure')
  const [kind, setKind] = useState<HrStructureKind>('department')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [formError, setFormError] = useState('')
  const kindRefs = useRef<Array<HTMLButtonElement | null>>([])
  const queryKey = ['hr-structure', companyId]
  const { data: records = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => listHrStructure(companyId),
  })
  const save = useMutation({
    mutationFn: async () => {
      const name = form.name.trim()
      if (!name) throw new Error('Enter a name.')
      if (form.kind === 'team' && !form.parentId) throw new Error('Choose the department this team belongs to.')
      const headcount = form.headcount.trim() ? Number(form.headcount) : undefined
      if (headcount !== undefined && (!Number.isInteger(headcount) || headcount < 0)) {
        throw new Error('Planned headcount must be a whole number of zero or more.')
      }
      const payload = {
        name,
        code: form.code.trim(),
        ...(form.kind === 'position' && headcount !== undefined && { headcount }),
        ...(form.kind === 'position' && form.vacancyState && { vacancyState: form.vacancyState as 'open' | 'filled' | 'paused' }),
      }
      return editingId
        ? updateHrStructure(companyId, editingId, { ...payload, parentId: form.parentId || null })
        : createHrStructure(companyId, { kind: form.kind, ...payload, ...(form.parentId && { parentId: form.parentId }) })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey })
      const info = kindInfo(form.kind)
      toast.success(`${form.name.trim()} ${editingId ? 'saved' : `added to ${info.label.toLowerCase()}`}`)
      setEditingId(null)
      setIsFormOpen(false)
      setForm({ ...emptyForm, kind })
      setFormError('')
    },
    onError: (saveError) => setFormError(getErrorMessage(saveError)),
  })
  const changeStatus = useMutation({
    mutationFn: (record: HrStructureRecord) =>
      updateHrStructure(companyId, record._id, { active: !record.active }),
    onSuccess: (_result, record) => {
      void queryClient.invalidateQueries({ queryKey })
      toast.success(record.active
        ? `${record.name} deactivated. People who already have it keep it on their record.`
        : `${record.name} is available again`)
    },
    onError: (statusError) => toast.error(getErrorMessage(statusError)),
  })

  const info = kindInfo(kind)
  const formInfo = kindInfo(form.kind)
  const countOf = (value: HrStructureKind): number =>
    records.filter((record) => record.kind === value && record.active).length
  // The API keeps department nesting to one level, so only top-level
  // departments can hold another department.
  const parentOptions = records.filter((record) =>
    record.kind === 'department' && record.active && record._id !== editingId &&
    (form.kind !== 'department' || !record.parentId)
  )
  const visible = records
    .filter((record) => record.kind === kind)
    .sort((a, b) => Number(b.active) - Number(a.active))
  const nameOf = (id?: string): string | undefined =>
    records.find((record) => record._id === id)?.name
  const childCount = (id: string): number =>
    records.filter((record) => record.parentId === id && record.active && record.kind !== 'position').length
  const needsDepartment = kind === 'team' && countOf('department') === 0

  const selectKind = (value: HrStructureKind): void => {
    setKind(value)
    setEditingId(null)
  }
  const moveKind = (event: React.KeyboardEvent, index: number): void => {
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key]
    if (!step) return
    event.preventDefault()
    const next = (index + step + KINDS.length) % KINDS.length
    selectKind(KINDS[next].kind)
    kindRefs.current[next]?.focus()
  }
  const startCreate = (): void => {
    setEditingId(null)
    setForm({ ...emptyForm, kind })
    setFormError('')
    setIsFormOpen(true)
  }
  const startEdit = (record: HrStructureRecord): void => {
    setEditingId(record._id)
    setForm({
      kind: record.kind,
      name: record.name,
      code: record.code ?? '',
      parentId: record.parentId ?? '',
      headcount: record.headcount?.toString() ?? '',
      vacancyState: record.vacancyState ?? '',
    })
    setFormError('')
    setIsFormOpen(true)
  }
  const closeForm = (): void => {
    if (!save.isPending) setIsFormOpen(false)
  }

  const details = (record: HrStructureRecord): string[] => {
    const parent = nameOf(record.parentId)
    const children = record.kind === 'department' ? childCount(record._id) : 0
    return [
      parent ? `In ${parent}` : undefined,
      children ? `${children} inside` : undefined,
      record.headcount !== undefined ? `${record.headcount} planned` : undefined,
      record.vacancyState ? VACANCY_LABELS[record.vacancyState] : undefined,
    ].filter((part): part is string => Boolean(part))
  }

  return (
    <div className="space-y-4">
      <SetupSectionIntro>
        Build these lists once for the company. You pick from them when you start someone’s
        employment, record a job change, or choose who a policy applies to.
      </SetupSectionIntro>
      <Card className="min-w-0" contentClassName="p-0">
        <div className="grid lg:grid-cols-[15rem_minmax(0,1fr)]">
          <div
            role="tablist"
            aria-label="Lists"
            aria-orientation="vertical"
            className="flex gap-1 overflow-x-auto border-b border-gray-200 p-2 [scrollbar-width:none] lg:flex-col lg:overflow-visible lg:border-b-0 lg:border-r dark:border-gray-700"
          >
            {KINDS.map((item, index) => {
              const selected = item.kind === kind
              const count = countOf(item.kind)
              return (
                <button
                  key={item.kind}
                  ref={(node) => { kindRefs.current[index] = node }}
                  type="button"
                  role="tab"
                  id={`structure-kind-${item.kind}`}
                  aria-selected={selected}
                  aria-controls="structure-kind-panel"
                  tabIndex={selected ? 0 : -1}
                  onClick={() => selectKind(item.kind)}
                  onKeyDown={(event) => moveKind(event, index)}
                  className={cn(
                    'flex shrink-0 items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                    selected
                      ? 'bg-primary/10 font-medium text-primary dark:text-sky-300'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50'
                  )}
                >
                  <span className="whitespace-nowrap">{item.label}</span>
                  <span
                    className={cn(
                      'min-w-6 rounded-full px-1.5 text-center text-xs tabular-nums',
                      selected ? 'bg-primary/10' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    )}
                  >
                    <span className="sr-only">, </span>{count}<span className="sr-only"> active</span>
                  </span>
                </button>
              )
            })}
          </div>

          <section
            id="structure-kind-panel"
            role="tabpanel"
            aria-labelledby={`structure-kind-${kind}`}
            className="min-w-0"
          >
            <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-5 dark:border-gray-700">
              <div className="min-w-0 max-w-xl">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{info.label}</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{info.description}</p>
              </div>
              {canEdit && !needsDepartment && visible.length > 0 && (
                <Button size="sm" className="self-start" onClick={startCreate}>
                  <Plus className="mr-1.5 h-4 w-4" aria-hidden />
                  Add {info.singular}
                </Button>
              )}
            </div>

            {isLoading ? (
              <div className="flex justify-center p-12" role="status">
                <Spinner />
                <span className="sr-only">Loading {info.label.toLowerCase()}</span>
              </div>
            ) : error ? (
              <div className="space-y-3 p-5" role="alert">
                <p className="text-sm text-danger">{getErrorMessage(error)}</p>
                <Button variant="secondary" size="sm" onClick={() => void refetch()}>Retry</Button>
              </div>
            ) : needsDepartment ? (
              <div className="flex flex-col items-start gap-3 px-5 py-8">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Add a department first. Every team belongs to one.
                </p>
                <Button size="sm" variant="secondary" onClick={() => selectKind('department')}>
                  Go to departments
                </Button>
              </div>
            ) : visible.length === 0 ? (
              <div className="flex flex-col items-start gap-3 px-5 py-8">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  No {info.label.toLowerCase()} yet.
                  {canEdit
                    ? ` Add the first one, for example ${info.example}, so you can assign people to it.`
                    : ' Someone with access to company structure can add them.'}
                </p>
                {canEdit && (
                  <Button size="sm" onClick={startCreate}>
                    <Plus className="mr-1.5 h-4 w-4" aria-hidden />
                    Add the first {info.singular}
                  </Button>
                )}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {visible.map((record) => {
                  const meta = details(record)
                  return (
                    <li
                      key={record._id}
                      className="group flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3 sm:px-5"
                    >
                      <div className={cn('min-w-0', !record.active && 'opacity-70')}>
                        <p className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-gray-100">{record.name}</span>
                          {record.code && (
                            <span className="rounded border border-gray-200 px-1.5 text-xs text-gray-600 dark:border-gray-600 dark:text-gray-300">
                              {record.code}
                            </span>
                          )}
                          {!record.active && <Badge variant="neutral" size="sm">Inactive</Badge>}
                        </p>
                        {meta.length > 0 && (
                          <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{meta.join(' · ')}</p>
                        )}
                      </div>
                      {canEdit && (
                        <div className="flex gap-1 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                          <Button variant="ghost" size="sm" onClick={() => startEdit(record)}>
                            Edit<span className="sr-only"> {record.name}</span>
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            disabled={changeStatus.isPending}
                            onClick={() => changeStatus.mutate(record)}
                          >
                            {record.active ? 'Deactivate' : 'Reactivate'}
                            <span className="sr-only"> {record.name}</span>
                          </Button>
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        </div>
      </Card>

      <Modal
        open={isFormOpen}
        onClose={closeForm}
        title={editingId ? `Edit ${formInfo.singular}` : `Add ${formInfo.singular}`}
        className="max-w-md"
        footer={
          <>
            <Button variant="secondary" disabled={save.isPending} onClick={closeForm}>Cancel</Button>
            <Button type="submit" form="hr-structure-form" loading={save.isPending}>
              {editingId ? 'Save changes' : `Add ${formInfo.singular}`}
            </Button>
          </>
        }
      >
        <form
          id="hr-structure-form"
          className="space-y-4"
          onSubmit={(event) => { event.preventDefault(); setFormError(''); save.mutate() }}
        >
          {!editingId && (
            <p className="text-sm text-gray-600 dark:text-gray-300">{formInfo.description}</p>
          )}
          <Input
            id="hr-structure-name" label="Name" required maxLength={120} autoFocus
            placeholder={`For example, ${formInfo.example}`}
            value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
          <Input
            id="hr-structure-code" label="Short code" maxLength={40}
            hint="Optional. A short reference such as FIN, shown next to the name."
            value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value })}
          />
          {['team', 'department', 'position'].includes(form.kind) && (
            <div>
              <Select
                label={form.kind === 'department' ? 'Inside another department' : 'Department'}
                required={form.kind === 'team'}
                value={form.parentId}
                onChange={(value) => setForm({ ...form, parentId: value })}
                options={parentOptions.map((record) => ({ value: record._id, label: record.name }))}
                placeholder={form.kind === 'team' ? 'Choose a department' : 'None'}
              />
              <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-300">
                {form.kind === 'team'
                  ? 'The department this team belongs to.'
                  : form.kind === 'department'
                    ? 'Optional. Leave as None for a top-level department.'
                    : 'Optional. The department this position sits in.'}
              </p>
            </div>
          )}
          {form.kind === 'position' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                id="hr-structure-headcount" label="Planned headcount" type="number" inputMode="numeric" min={0} step={1}
                hint="How many people this seat needs."
                value={form.headcount} onChange={(event) => setForm({ ...form, headcount: event.target.value })}
              />
              <Select
                label="Vacancy"
                value={form.vacancyState}
                onChange={(value) => setForm({ ...form, vacancyState: value })}
                options={Object.entries(VACANCY_LABELS).map(([value, label]) => ({ value, label }))}
                placeholder="Not set"
              />
            </div>
          )}
          {editingId && (
            <p className="text-xs text-gray-600 dark:text-gray-300">
              To stop using it, deactivate it from the list instead. People who already have it
              keep it on their record.
            </p>
          )}
          {formError && <p role="alert" className="text-sm text-danger">{formError}</p>}
        </form>
      </Modal>
    </div>
  )
}
